
import cloneDeep from 'lodash/cloneDeep';
import UrlParse from 'url-parse';

const PARAM_REGEX = /{([_$a-zA-Z]\w*)}/g;

/**
 * 将约定好格式的url字符串分解成请求地址和参数信息
 * 只用于请求链接的拆分，因此不用考虑hash
 * 注：queryInfo参数信息键值对有两种可能，如果值为'{a}'意为需要取变量a的值
 *      否则为定死的常量值，与变量无关
 * @param {String} urlString url字符串
 * @return {Object {url, queryInfo}}
 * url为发送请求的地址，queryInfo为参数键名和取值变量的键值对
 */
export const urlStringToRequestDetail = (urlString = '') => {
    if (!urlString || typeof urlString !== 'string') {
        return {
            url: null,
            queryInfo: null,
        };
    }
    const urlParse = new UrlParse(urlString, true);
    return {
        url: urlParse.pathname,
        queryInfo: urlParse.query,
    };
};

/**
 * 根据参数信息组装请求数据
 * 组装规则为：
 * 取originPayload的值为初始值
 * 如果有形如'{a}'的值，则需要取datasource.a的数据填入payload中
 * 否则直接赋值给相应键名
 * @param {Object} queryInfo 通过urlStringToRequestDetail生成的参数信息
 * @param {Object} datasource 从何处取值
 * @param {Object} originPayload 原有的参数
 * @return {Object} 构造好的参数对象
 */
export const composeUrlPayload = (queryInfo, datasource, originPayload = {}) => {
    const payload = cloneDeep(originPayload);
    if (!queryInfo) {
        return payload;
    }

    Object.keys(queryInfo).forEach(key => {
        if (queryInfo[key].match(PARAM_REGEX)) {
            // 处理通配符‘{a}’
            const paramKey = queryInfo[key].slice(1, -1);
            payload[key] = datasource[paramKey];
        } else {
            payload[key] = queryInfo[key];
        }
    });
    return payload;
};

/**
 * 根据参数信息组装url
 * 组装规则为：
 * 将url中的'{a}'替换为datasource.a
 * @param {String} urlString url字符串
 * @param {Object} datasource 取值来源
 * @return {String} 组装完成的字符串
 */
export const composeUrlWithParam = (url = '', datasource) => {
    if (!url) {
        return '';
    }
    const paramArray = url.match(PARAM_REGEX);
    if (paramArray) {
        if (paramArray[0] === url) {
            // 如果整个字符串都是{a}，则替换整个url
            return url.replace(PARAM_REGEX, (match, string) => {
                if (typeof datasource[string] === 'undefined') {
                    return '';
                }
                /**
                 * admin遇到一种情况是url本身已经是encode过的，直接encode会造成两次encode
                 * @zhangbin03 斌哥灵机一动提出可以先decode再encode
                 * 整个url的替换用encodeURI
                 */
                return encodeURI(decodeURI(datasource[string]));
            });
        }

        const newUrl = url.replace(PARAM_REGEX, (match, string) => {
            if (typeof datasource[string] === 'undefined') {
                return '';
            }
            /**
             * 先decode再encode的原因同上
             * 到这里说明是局部url的替换，所以用encodeURIComponent
             */
            return encodeURIComponent(decodeURIComponent(datasource[string]));
        });

        return newUrl;
    }
    return url;
};

/**
 * 将url和请求数据组装成链接地址
 * 若openAllParams未开启则使用正则替换
 * 否则调用url-parse完成
 * @param {String} url 形如'a/b/c'的地址
 * @param {Object} datasource 数据源
 * @param {Boolean} openAllParams 附带所有参数
 * @return {String} 形如'a/b/c?x=1&y=2'的链接地址
 */
export const composeUrlHref = (url = '', openAllParams = false, datasource = {}) => {
    // 先把需要嵌套的变量处理了
    let href = composeUrlWithParam(url, datasource);
    if (openAllParams) {
        // 需要自行组装一份query
        const urlParse = new UrlParse(href);
        let originQuery; let originQueryString;
        if (urlParse.hash) {
            // 有hash则处理hash中的query
            const hashUrlParse = new UrlParse(urlParse.hash.replace('#', ''));
            originQuery = UrlParse.qs.parse(hashUrlParse.query);
            originQueryString = hashUrlParse.query;
        } else {
            // 无hash则使用解析出来的query
            originQuery = UrlParse.qs.parse(urlParse.query);
            originQueryString = urlParse.query;
        }
        // 组装新的query
        const newQueryString = '?' + UrlParse.qs.stringify({
            ...datasource,
            ...originQuery,
        });
        let newHref;
        if (originQueryString) {
            // 原有query，采用替换方式
            const originQueryStringIndex = href.lastIndexOf(originQueryString);
            newHref = href.substring(0, originQueryStringIndex)
                + newQueryString + href.substring(originQueryStringIndex + originQueryString.length);
        } else {
            newHref = href + newQueryString;
        }
        href = newHref;
    }
    // 直接使用正则替换后的字符串
    return href;
};

export function parse(str) {
    if (!str) {
        return {};
    }
    return str.split('&').reduce((pre, cur) => {
        const [key = '', value = ''] = cur.split('=');
        pre[key] = value;
        return pre;
    }, {});
}

export function getUrlParam(name) {
    const res = parse(location.search.slice(1));

    const hashSearchIndex = location.hash && location.hash.indexOf('?') || 0;

    // 优先哈希中的参数
    if (~hashSearchIndex) {
        Object.assign(res, parse(location.hash && location.hash.slice(hashSearchIndex + 1)));
    }
    return `${res[name] || ''}`;
}